import { useState } from 'react';
import { Button, DatePicker, Descriptions, Input, Table, message } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import useFinanceTable from '../../../hooks/useFinanceTable';
import { incomeDetail, incomeExport, incomeList } from '../../../api/finance';
import FinanceExportButton from '../../../components/finance/FinanceExportButton';
import FinanceDetailModal from '../../../components/finance/FinanceDetailModal';
import { can } from '../../../util/permission';
import { formatDateTime, formatMoney, formatPercent } from '../../../util/finance';

export default function IncomeTab() {
  const table = useFinanceTable(incomeList);
  const [form, setForm] = useState({ taxMonth: null, realName: '', idCardNo: '', userId: '' });
  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const search = () => {
    table.search({
      taxMonth: form.taxMonth ? dayjs(form.taxMonth).format('YYYY-MM') : undefined,
      realName: form.realName || undefined,
      idCardNo: form.idCardNo || undefined,
      userId: form.userId || undefined,
    });
  };

  const reset = () => {
    setForm({ taxMonth: null, realName: '', idCardNo: '', userId: '' });
    table.reset();
  };

  const openDetail = async (record) => {
    try {
      const res = await incomeDetail(record.snapshotId);
      setDetail(res);
      setDetailOpen(true);
    } catch (error) {
      message.error(error?.message || '查询详情失败');
    }
  };

  const columns = [
    { title: '所属月份', dataIndex: 'taxMonth', width: 100, fixed: 'left' },
    { title: '用户ID', dataIndex: 'userId', width: 100 },
    { title: '姓名', dataIndex: 'realName', width: 100 },
    { title: '身份证号', dataIndex: 'idCardNo', width: 180 },
    { title: '手机号', dataIndex: 'phone', width: 130 },
    { title: '累计税前收入', dataIndex: 'cumulativeIncome', width: 130, render: formatMoney },
    { title: '累计费用扣除', dataIndex: 'expenseDeduction', width: 130, render: formatMoney },
    { title: '累计减除费用', dataIndex: 'basicDeduction', width: 130, render: formatMoney },
    { title: '累计应纳税所得额', dataIndex: 'taxableIncome', width: 150, render: formatMoney },
    { title: '预扣率', dataIndex: 'taxRate', width: 90, render: formatPercent },
    { title: '本笔扣税', dataIndex: 'currentTax', width: 110, render: formatMoney },
    { title: '累计已扣税', dataIndex: 'cumulativeTax', width: 120, render: formatMoney },
    { title: '税后入账', dataIndex: 'netAmount', width: 120, render: formatMoney },
    { title: '入账时间', dataIndex: 'entryTime', width: 170, render: formatDateTime },
    {
      title: '操作',
      key: 'action',
      width: 90,
      fixed: 'right',
      render: (_, record) =>
        can('finance:income:detail') ? (
          <Button type="link" onClick={() => openDetail(record)}>
            详情
          </Button>
        ) : null,
    },
  ];

  return (
    <div>
      <div className="finance-toolbar">
        <DatePicker
          picker="month"
          value={form.taxMonth}
          onChange={(value) => setForm({ ...form, taxMonth: value })}
          placeholder="所属月份"
          style={{ width: 140 }}
        />
        <Input
          value={form.realName}
          onChange={(e) => setForm({ ...form, realName: e.target.value })}
          placeholder="姓名"
          style={{ width: 140 }}
          allowClear
        />
        <Input
          value={form.idCardNo}
          onChange={(e) => setForm({ ...form, idCardNo: e.target.value })}
          placeholder="身份证号（精确）"
          style={{ width: 200 }}
          allowClear
        />
        <Input
          value={form.userId}
          onChange={(e) => setForm({ ...form, userId: e.target.value.replace(/\D/g, '') })}
          placeholder="用户ID"
          style={{ width: 120 }}
          allowClear
        />
        <Button type="primary" icon={<SearchOutlined />} onClick={search}>
          查询
        </Button>
        <Button onClick={reset}>重置</Button>
        {can('finance:income:export') && (
          <FinanceExportButton exportApi={incomeExport} query={table.query} />
        )}
      </div>
      <Table
        className="finance-table"
        columns={columns}
        dataSource={table.data}
        rowKey="snapshotId"
        loading={table.loading}
        scroll={{ x: 1850 }}
        rowClassName={(_, index) => (index % 2 === 1 ? 'finance-row-odd' : '')}
        pagination={{
          current: table.pagination.current,
          pageSize: table.pagination.pageSize,
          total: table.total,
          showSizeChanger: true,
          showTotal: (total) => `共 ${total} 条数据`,
          pageSizeOptions: ['10', '20', '50', '100'],
          onChange: table.onTableChange,
        }}
      />
      <FinanceDetailModal
        open={detailOpen}
        title="计税快照详情"
        onClose={() => setDetailOpen(false)}
      >
        {detail && (
          <>
            <Descriptions bordered size="small" column={2}>
              <Descriptions.Item label="所属月份">{detail.taxMonth || '-'}</Descriptions.Item>
              <Descriptions.Item label="用户ID">{detail.userId || '-'}</Descriptions.Item>
              <Descriptions.Item label="姓名">{detail.realName || '-'}</Descriptions.Item>
              <Descriptions.Item label="身份证号">{detail.idCardNo || '-'}</Descriptions.Item>
              <Descriptions.Item label="订单号">{detail.orderNo || '-'}</Descriptions.Item>
              <Descriptions.Item label="任务名称">{detail.taskTitle || '-'}</Descriptions.Item>
              <Descriptions.Item label="本笔税前赏金">{formatMoney(detail.grossAmount)}</Descriptions.Item>
              <Descriptions.Item label="税后入账金额">{formatMoney(detail.netAmount)}</Descriptions.Item>
              <Descriptions.Item label="入账时间">{formatDateTime(detail.entryTime)}</Descriptions.Item>
              <Descriptions.Item label="连续劳务月份">{detail.continuousMonths ?? '-'}</Descriptions.Item>
            </Descriptions>
            <div className="finance-calculation">
              <h4>累计预扣法计算过程</h4>
              <div>本笔入账前累计收入：{formatMoney(detail.ytdIncomeBefore)}</div>
              <div>本笔入账后累计收入：{formatMoney(detail.ytdIncomeAfter)}</div>
              <div>累计费用扣除（收入 × 20%）：{formatMoney(detail.expenseDeduction)}</div>
              <div>累计减除费用（5000 × {detail.continuousMonths || 0} 个月）：{formatMoney(detail.basicDeduction)}</div>
              <div>累计应纳税所得额：{formatMoney(detail.taxableIncome)}</div>
              <div>适用预扣率：{formatPercent(detail.taxRate)}</div>
              <div>速算扣除数：{formatMoney(detail.quickDeduction)}</div>
              <div>累计应纳税额：{formatMoney(detail.cumulativeTax)}</div>
              <div>本笔应扣税：{formatMoney(detail.currentTax)}</div>
            </div>
          </>
        )}
      </FinanceDetailModal>
    </div>
  );
}
